import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Button, Callout, Card, DataListItem, toast } from '@node-space/ui-components'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { generateReference } from 'utils/utils'
import { EARN_PRODUCT_PROVIDER_NAME, EARN_REFERENCE_PREFIX } from '../../constants/const'
import { useEarnSheetContext } from '../../context/EarnSheetProvider'
import { useEarnAssetDetailsQuery } from '../../hooks/api/useEarnAssetDetailsQuery'
import { useWithdrawPortfolio } from '../../hooks/api/useWithdrawPortfolio'
import { useEarnTracking } from '../../hooks/useEarnTracking'
import { EarnWithdrawFormData, TransferStatus } from '../../types'
import { invalidateEarnQueries, prepareWithdrawReviewData } from '../../utils/utils'
import { EarnAmountInput } from './EarnAmountInput'
import { EarnCurrencySelect } from './EarnCurrencySelect'
import { EarnReview } from './EarnReview'
import { EarnWalletSelect } from './EarnWalletSelect'
import { getEarnWithdrawValidationSchema } from './validationSchema'

type WithdrawStep = 'form' | 'review'

export const EarnWithdraw = () => {
  const { t } = useTranslation()
  const { requestError, setRequestError } = useMappedErrorMessage()
  const reactQueryClient = useQueryClient()
  const { trackFormInitiate, trackFormCancel } = useEarnTracking()

  const { earnSheetAction, earnProducts, handleSheetUpdate } = useEarnSheetContext()
  const { selectedCurrency: selectedCurrencyFromAction, onClose } = earnSheetAction || {}
  const maxAmountRef = useRef('')
  const withdrawReferenceRef = useRef('')
  const [currentStep, setCurrentStep] = useState<WithdrawStep>('form')

  useEffect(() => {
    trackFormInitiate('withdraw', { currency: selectedCurrencyFromAction })
  }, [trackFormInitiate, selectedCurrencyFromAction])

  const showCurrencySelect = !selectedCurrencyFromAction && earnProducts.length > 1
  const defaultCurrency =
    selectedCurrencyFromAction || (earnProducts.length === 1 ? earnProducts[0].asset : '')

  const formMethods = useForm<EarnWithdrawFormData>({
    resolver: yupFormResolver<EarnWithdrawFormData>(
      getEarnWithdrawValidationSchema(t, () => maxAmountRef.current)
    ),
    mode: 'onChange',
    defaultValues: {
      currency: defaultCurrency,
      withdrawToWallet: null,
      amount: '',
    },
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    watch,
  } = formMethods

  const selectedCurrency = watch('currency')
  const selectedAsset = earnProducts.find(asset => asset.asset === selectedCurrency)
  const portfolioId = selectedAsset?.portfolioId ?? undefined

  const {
    data: assetDetails,
    isLoading: isLoadingAssetDetails,
    isError: isAssetDetailsError,
  } = useEarnAssetDetailsQuery(portfolioId)

  const maxAmount = assetDetails?.fundsOverview.allocatedFunds || ''

  useEffect(() => {
    maxAmountRef.current = maxAmount
  }, [maxAmount])

  useEffect(() => {
    if (!selectedCurrencyFromAction && selectedCurrency) {
      setValue('withdrawToWallet', null)
      setValue('amount', '')
    }
  }, [selectedCurrency, selectedCurrencyFromAction, setValue])

  const { withdrawFromPortfolio, isWithdrawingFromPortfolioPending } = useWithdrawPortfolio({
    onSuccess: data => {
      invalidateEarnQueries(reactQueryClient, data.portfolioId)

      if (data.status === TransferStatus.FAILED) {
        setRequestError({
          show: true,
          handledMessage: t('earn.withdrawSheet.withdrawStatusFailed'),
        })
        return
      }

      onClose?.()
      toast({
        variant: 'success',
        header: t('earn.withdrawSheet.successNotificationTitle', {
          currency: `${data.withdrawalAmount.amount} ${data.withdrawalAmount.currency}`,
        }),
      })
    },
    onError: error => {
      setRequestError({
        show: true,
        errorCode: error?.status,
        handledMessage: t('earn.withdrawSheet.withdrawFailed'),
      })
    },
  })

  const isPending = isWithdrawingFromPortfolioPending

  const onReviewBack = useCallback(() => {
    setCurrentStep('form')
  }, [])

  const hasSheetAction = !!earnSheetAction

  useEffect(() => {
    if (!hasSheetAction) return
    if (currentStep === 'review') {
      handleSheetUpdate({
        header: t('earn.withdrawSheet.reviewHeader'),
        onBack: onReviewBack,
      })
    } else {
      handleSheetUpdate({
        header: t('earn.withdrawSheet.header'),
        onBack: undefined,
      })
    }
  }, [currentStep, t, handleSheetUpdate, hasSheetAction, onReviewBack])

  const onCancel = () => {
    trackFormCancel('withdraw', { currency: selectedCurrency })
    onClose?.()
  }

  const onSubmit = () => {
    setRequestError({
      show: false,
    })
    withdrawReferenceRef.current = generateReference(EARN_REFERENCE_PREFIX)
    setCurrentStep('review')
  }

  const onConfirmWithdraw = () => {
    setRequestError({
      show: false,
    })

    if (!portfolioId) {
      setRequestError({
        show: true,
        errorCode: 400,
        handledMessage: t('earn.withdrawSheet.portfolioNotFound'),
      })
      return
    }

    const data = formMethods.getValues()

    withdrawFromPortfolio({
      reference: withdrawReferenceRef.current,
      portfolioId,
      walletId: data.withdrawToWallet?.value || '',
      amount: data.amount,
    })
  }

  if (currentStep === 'review') {
    return (
      <EarnReview
        data={prepareWithdrawReviewData(formMethods.getValues(), selectedAsset)}
        requestError={requestError}
        isPending={isPending}
        onCancel={onCancel}
        onConfirm={onConfirmWithdraw}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <Box
        tag="form"
        flexCol
        gapY={16}
        width="full"
        paddingY={24}
        onSubmit={handleSubmit(onSubmit)}
      >
        {requestError?.show && (
          <Callout variant="error" testId="withdraw-error-callout">
            {requestError?.handledMessage}
          </Callout>
        )}

        {isAssetDetailsError && (
          <Callout variant="error" testId="asset-details-error-callout">
            {t('earn.withdrawSheet.assetDetailsError')}
          </Callout>
        )}

        {showCurrencySelect && <EarnCurrencySelect disabled={isPending} />}

        {!showCurrencySelect && selectedCurrency && (
          <Card variant="static" emphasis full>
            <DataListItem
              id="withdraw-from-asset"
              title={t('earn.withdrawSheet.withdrawFromAsset')}
              BodyAvatar={<Avatar type="currency" name={selectedCurrency} size="small" />}
              body={
                selectedAsset
                  ? `${selectedCurrency} (${EARN_PRODUCT_PROVIDER_NAME})`
                  : selectedCurrency
              }
              layout="vertical"
            />
          </Card>
        )}

        <EarnAmountInput
          selectedCurrency={selectedCurrency}
          maxAmount={maxAmount}
          disabled={!selectedCurrency || isPending || isLoadingAssetDetails || isAssetDetailsError}
        />

        <EarnWalletSelect
          selectedCurrency={selectedCurrency}
          name="withdrawToWallet"
          disabled={!selectedCurrency || isPending}
          testId="withdraw-to-wallet-combobox"
          label={t('earn.withdrawSheet.withdrawToWallet')}
          loadErrorMessage={t('earn.withdrawSheet.walletsLoadError')}
        />

        <Box flex justify="between" gapX={12} paddingT={24}>
          <Button onClick={onCancel} variant="secondary" type="button" testId="cancel-button">
            {t('cancel')}
          </Button>

          <Button
            variant="primary"
            type="submit"
            testId="withdraw-button"
            disabled={isSubmitting || isPending}
            loading={isSubmitting || isPending}
          >
            {t('earn.withdraw')}
          </Button>
        </Box>
      </Box>
    </FormProvider>
  )
}
